import { mapPxPerProjectedMetre } from './mapLines'
import { pageMm } from './printPaper'
import type { PaperFormat, PrintOrientation } from './printPaper'

/**
 * What the print server will actually render.
 *
 * `SERVER_MAX_SCALE` disagrees with the published API documentation, which
 * states 1:1 000 - 1:500 000. The server is authoritative and it is the
 * narrower of the two: a 1:500 000 job submitted on 2026-09-12 was ACCEPTED at
 * submission and then failed at render with
 *
 *     scale 1:500000 exceeds the supported range (max 1:250000)
 *
 * which is the worst possible shape for a limit — the user waits for a render
 * that was never going to happen. Do not widen this to match the document.
 */
export const SERVER_MIN_SCALE = 1000
export const SERVER_MAX_SCALE = 250000
export const SERVER_MAX_PAGE_MM = 2000

/**
 * Cartographic print scales, finest first.
 *
 * A server-rendered print has a real scale — one paper millimetre is a fixed
 * number of ground metres — which is a different quantity from the slippy zoom
 * the on-screen map runs on. Restricting it to a short list of conventional
 * scales is what makes a printed sheet measurable: a hunter can lay a ruler on
 * a 1:10 000 sheet and read distance off it, which is impossible when the scale
 * is whatever zoom the window happened to be at.
 *
 * The print server renders 1:1 000 – 1:250 000 (`SERVER_MAX_SCALE`, which is
 * narrower than the published documentation); these five include 1:100 000 at
 * the coarse end. It is cheaper to render than 1:50 000 (37 s against 182 s at
 * 1000 × 1000 mm) because map features drop out past 1:50 000, so there is
 * no render-cost reason to withhold it.
 */
export const PRINT_SCALES = [5000, 10000, 20000, 50000, 100000] as const

export type PrintScale = (typeof PRINT_SCALES)[number]

/**
 * Margin left on every edge of the sheet, in millimetres.
 *
 * Not decoration: consumer printers cannot print to the sheet edge, and a map
 * drawn into that dead zone is silently cropped by the printer rather than
 * scaled to fit. The print server takes margins explicitly, so the ground
 * extent is computed from the printable rectangle rather than the paper.
 */
export const DEFAULT_PRINT_MARGIN_MM = 10

/** "1:10 000" — grouped so a five- or six-digit scale stays readable. */
export function formatPrintScale(scale: number): string {
  return `1:${String(Math.round(scale)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`
}

export interface PrintSheetSpec {
  /**
   * Optional because `page` alone is enough to measure a sheet — required only
   * when `page` is absent, which `printableAreaMm` cannot express in the type
   * (TS has no "one of these two" shape cheap enough to be worth it here). A
   * caller holding only millimetres (`mapPrintLimits`'s `MapPrintLimitCheck`,
   * for one) should not have to invent a format just to satisfy this type.
   */
  format?: PaperFormat
  /** Optional for the same reason `format` is: irrelevant once `page` is set. */
  orientation?: PrintOrientation
  /** Margin per edge in mm. Defaults to {@link DEFAULT_PRINT_MARGIN_MM}. */
  marginMm?: number
  /**
   * Explicit page millimetres, which WIN over `format`/`orientation`.
   *
   * A custom sheet has no entry in the paper table and no orientation to apply —
   * it states both dimensions directly. Carrying it as an override rather than
   * as a second sheet type means every consumer of this spec (the frame, the
   * limits, the payload) keeps one code path.
   */
  page?: { w: number; h: number }
}

export interface Extent2 {
  w: number
  h: number
}

/** The rectangle the map actually occupies on the sheet, in millimetres. */
export function printableAreaMm({
  format,
  orientation = 'portrait',
  marginMm = DEFAULT_PRINT_MARGIN_MM,
  page,
}: PrintSheetSpec): Extent2 {
  // The 'a4' fallback here is not a real answer, only a value `pageMm` (which
  // takes a required PaperFormat) needs syntactically. It is unreachable in
  // practice: every caller either passes `page` or a real `format` — the
  // combination this exists to allow is "page but no format", which never
  // reaches the fallback because `page` is checked first.
  const sheet = page ?? pageMm(format ?? 'a4', orientation)
  return {
    w: Math.max(0, sheet.w - 2 * marginMm),
    h: Math.max(0, sheet.h - 2 * marginMm),
  }
}

/**
 * The ground area that printable rectangle covers at a given scale, in metres.
 *
 * This is the whole point of a fixed scale: for a chosen scale and sheet the
 * covered ground is a constant, so the preview frame can be derived from the
 * print settings rather than from the window's current zoom.
 */
export function groundExtentMetres({
  scale,
  ...sheet
}: PrintSheetSpec & { scale: number }): Extent2 {
  const area = printableAreaMm(sheet)
  // Multiply before dividing: `(277 / 1000) * 10000` lands on 2770.0000000000005,
  // and a ground extent carrying float dust propagates into the preview frame.
  return { w: (area.w * scale) / 1000, h: (area.h * scale) / 1000 }
}

/**
 * That ground extent expressed in on-screen CSS pixels, so the preview frame
 * can show exactly what will land on the paper.
 *
 * The two factors are separate quantities and both are needed. `zoom` gives
 * pixels per *projected* metre in Web Mercator. The `1/cos(lat)` term converts
 * true ground metres into projected metres: Web Mercator stretches distance
 * away from the equator, so at 65°N a kilometre of ground occupies ~2.4× the
 * pixels it does at the equator. The print itself has no such distortion — the
 * server renders in the national projection at true scale — which is precisely
 * why the conversion has to happen here rather than being ignored.
 */
export function groundExtentCssPx({
  metres,
  lat,
  zoom,
}: {
  metres: Extent2
  lat: number
  zoom: number
}): Extent2 {
  const pxPerProjectedMetre = mapPxPerProjectedMetre(zoom)
  const cos = Math.cos((lat * Math.PI) / 180)
  const projectedPerGround = cos > 0 ? 1 / cos : 0
  const factor = projectedPerGround * pxPerProjectedMetre
  return { w: metres.w * factor, h: metres.h * factor }
}

/**
 * Timed renders of one sheet at every scale, in seconds.
 *
 * All six are the same job — a 1000 × 1000 mm sheet of the basic base map with
 * no features drawn on it, {@link REFERENCE_SHEET_M2} of printable paper — so
 * the only thing that varies between rows is the scale.
 *
 * The curve is NOT monotonic, and that is the whole reason this table exists.
 * Cost climbs to a peak at 1:50 000 and then collapses: past that scale the
 * cartographer's rules drop most of the detail from the source data, so a
 * 1:100 000 sheet covers four times the ground of a 1:50 000 one and takes a
 * fifth of the time. Ground area, the obvious proxy, gets this exactly
 * backwards at the coarse end.
 */
const MEASURED_SECONDS: ReadonlyArray<readonly [scale: number, seconds: number]> = [
  [5000, 23],
  [10000, 31],
  [20000, 50],
  [50000, 182],
  [100000, 37],
  [200000, 36],
]

/** Printable m² of the sheet every {@link MEASURED_SECONDS} row was timed on. */
const REFERENCE_SHEET_M2 = 0.9604

/**
 * How cost grows with the paper, holding the scale fixed.
 *
 * Sub-linear, measured rather than assumed: the same 1:50 000 render took 182 s
 * on 1000 × 1000 mm and 483 s on 2000 × 2000 mm, which is 4.08× the paper for
 * 2.65× the time — log 2.65 / log 4.08 ≈ 0.69. Per-job overhead that does not
 * grow with the sheet (fetching the source data, starting the renderer) is what
 * keeps it under 1.
 */
const AREA_EXPONENT = 0.69

/**
 * What {@link MEASURED_SECONDS} says about a scale, including ones not in it.
 *
 * Interpolated in log space between the two neighbouring measurements, because
 * the measured scales are a geometric series and a scale sits between its
 * neighbours multiplicatively, not additively. A custom scale series is coming,
 * so this has to answer for any scale the server accepts — but every value it
 * returns between rows is INTERPOLATION, not something anyone timed.
 *
 * Clamped to the end rows outside the measured span. At the fine end that is
 * mild: 1:5 000 is already close to the floor of what the renderer costs. At
 * the coarse end the last two rows are 37 s and 36 s, so the curve has clearly
 * flattened and holding it flat to the server's 1:250 000 ceiling is safe.
 */
function referenceSeconds(scale: number): number {
  const [firstScale, firstSeconds] = MEASURED_SECONDS[0]
  const [lastScale, lastSeconds] = MEASURED_SECONDS[MEASURED_SECONDS.length - 1]
  // Written as a negated `>` so a non-finite scale lands here rather than
  // falling through the loop and returning nothing sensible.
  if (!(scale > firstScale)) return firstSeconds
  if (scale >= lastScale) return lastSeconds

  for (let i = 1; i < MEASURED_SECONDS.length; i += 1) {
    const [upperScale, upperSeconds] = MEASURED_SECONDS[i]
    if (scale >= upperScale) continue
    const [lowerScale, lowerSeconds] = MEASURED_SECONDS[i - 1]
    const t = Math.log(scale / lowerScale) / Math.log(upperScale / lowerScale)
    return lowerSeconds + t * (upperSeconds - lowerSeconds)
  }
  return lastSeconds
}

/**
 * How long the print server is expected to spend on this sheet, in seconds.
 *
 * Scale and paper are separate factors and both are needed: the scale decides
 * how much work each square metre of paper is, and the paper decides how many
 * square metres there are. Covered GROUND — the product of the two — is not a
 * usable proxy for either, since the same ground area is cheap when it comes
 * from a coarse scale and expensive when it comes from a large sheet.
 *
 * Exported so the bands below can be checked against the measurements they came
 * from; the panel itself only ever shows a band.
 */
export function estimatePrintSeconds({
  scale,
  ...sheet
}: PrintSheetSpec & { scale: number }): number {
  const area = printableAreaMm(sheet)
  const areaM2 = (area.w / 1000) * (area.h / 1000)
  return referenceSeconds(scale) * (areaM2 / REFERENCE_SHEET_M2) ** AREA_EXPONENT
}

/**
 * How long a print of this sheet is likely to take, as a band to show the user.
 *
 * The thresholds are where each band's wording stops being honest rather than
 * where the arithmetic is neat. Past 45 s "ready in seconds" is a promise the
 * render will not keep; past 5 minutes "a few minutes" is one, and the user
 * should be told to leave the page rather than sit in front of it.
 *
 * `long` is currently UNREACHABLE and deliberately kept: the most expensive
 * sheet the five A-formats can express is A0 at 1:50 000, about 3 minutes.
 * Custom page sizes up to the server's 2000 mm reach 483 s at the same scale,
 * which is the measurement the top band is sized for, so the band goes live
 * with them rather than being written then.
 */
export type PrintWaitBand = 'seconds' | 'minutes' | 'long'

const SECONDS_BAND_LIMIT_S = 45
const MINUTES_BAND_LIMIT_S = 300

export function estimatePrintWait(sheet: PrintSheetSpec & { scale: number }): PrintWaitBand {
  const seconds = estimatePrintSeconds(sheet)
  if (seconds < SECONDS_BAND_LIMIT_S) return 'seconds'
  if (seconds < MINUTES_BAND_LIMIT_S) return 'minutes'
  return 'long'
}

/**
 * The ground a sheet covers, as a "width × height" pair in kilometres.
 *
 * Shown because a scale alone does not tell a hunter what lands on the paper —
 * "1:20 000 on A3" is arithmetic, "5.9 × 8.4 km" is the hunting ground. One
 * decimal below 10 km, whole kilometres above it, where a tenth is noise.
 */
export function formatGroundCoverageKm(metres: Extent2): { width: string; height: string } {
  const format = (m: number): string => {
    const km = m / 1000
    return km < 10 ? km.toFixed(1) : Math.round(km).toString()
  }
  return { width: format(metres.w), height: format(metres.h) }
}

/**
 * The steps a custom scale may take: {1, 1.5, 2, 2.5, 3.75, 5, 7.5} per decade,
 * from the server's minimum to its maximum.
 *
 * Conventional printed-map scales rather than a constant ratio, so the steps
 * are deliberately uneven (1.5x, 1.33x, 1.25x, 1.5x, ...). 3.75 is the one
 * addition to the convention, smoothing the jump from 2.5 to 5.
 */
const DECADE_STEPS = [1, 1.5, 2, 2.5, 3.75, 5, 7.5]

export const CUSTOM_PRINT_SCALES: number[] = (() => {
  const out: number[] = []
  for (let decade = 1000; decade <= SERVER_MAX_SCALE; decade *= 10) {
    for (const step of DECADE_STEPS) {
      const scale = Math.round(decade * step)
      if (scale >= SERVER_MIN_SCALE && scale <= SERVER_MAX_SCALE) out.push(scale)
    }
  }
  return out
})()

/**
 * The offered step closest to `raw`, or — with `'coarser'` — the closest step
 * at least as coarse as `raw`.
 *
 * `'coarser'` is for the case where zoom drives the scale: the sheet has to
 * COVER what the user framed, and a step finer than the view would crop it.
 */
export function snapPrintScale(
  raw: number,
  direction: 'nearest' | 'coarser' | 'finer' = 'nearest',
): number {
  const first = CUSTOM_PRINT_SCALES[0]
  const last = CUSTOM_PRINT_SCALES[CUSTOM_PRINT_SCALES.length - 1]
  // The three non-finite inputs are not the same "out of range" case:
  // +Infinity means "an unbounded amount of ground to cover" (e.g. a viewport
  // whose printable dimension collapsed to ~0 in `scaleForViewport`'s
  // division), so it must clamp to the COARSEST scale — snapping it to the
  // finest would print a tiny patch of ground for a caller asking to cover an
  // enormous one, the worst possible answer. -Infinity and NaN carry no
  // meaningful magnitude at all (a caller passing a broken or absent number),
  // so they fall back to the finest scale rather than silently handing out
  // the coarsest, most ground-losing sheet by default.
  if (raw === Infinity) return last
  if (!Number.isFinite(raw) || raw <= first) return first
  if (raw >= last) return last

  if (direction === 'coarser') {
    return CUSTOM_PRINT_SCALES.find((s) => s >= raw) ?? last
  }
  if (direction === 'finer') {
    // Walked from the coarse end, so the first member at or below `raw` is the
    // coarsest one that still fits inside it.
    for (let i = CUSTOM_PRINT_SCALES.length - 1; i >= 0; i -= 1) {
      if (CUSTOM_PRINT_SCALES[i] <= raw) return CUSTOM_PRINT_SCALES[i]
    }
    return first
  }
  return CUSTOM_PRINT_SCALES.reduce((best, s) =>
    Math.abs(s - raw) < Math.abs(best - raw) ? s : best, first)
}
